import { ReportShapeItem, ShapeType } from '../../reportsTypes';
import {
  GeoJsonDictionary,
  ShapeDictionaryType,
  StringDictionary,
} from '../../utils';
import { useCallback, useMemo } from 'react';
import { createDynamicShapeZodSchema } from '../utils/createDynamicShapeZodSchema';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';

type NewDictionary = Record<string, unknown>;

const coerceIdToString = (value: unknown): string | undefined => {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : undefined;
  }

  if (typeof value === 'number') {
    return Number.isNaN(value) ? undefined : String(value);
  }

  return undefined;
};

export type ReportTemplateShapeValues = z.infer<
  ReturnType<typeof createDynamicShapeZodSchema>
>;

const addMultiShapeItemsToForm = (
  shapeItems: ReportShapeItem[],
  newDictionary: NewDictionary,
  shapeDictionary: GeoJsonDictionary | null
) => {
  if (!shapeDictionary) {
    shapeItems.forEach((shapeItem) => {
      newDictionary[shapeItem.name] = [];
    });
  } else {
    Object.keys(shapeDictionary).forEach((key) => {
      const featureCollection = shapeDictionary[key];
      newDictionary[key] = [];

      if (!featureCollection.features) {
        return;
      }

      if (!Array.isArray(newDictionary[key])) {
        return;
      }

      featureCollection.features.forEach((shape) => {
        const properties = shape.properties;

        if (
          properties &&
          properties.id !== undefined &&
          properties.id !== null
        ) {
          const id = coerceIdToString(properties.id);
          if (id) {
            (newDictionary[key] as string[]).push(id);
          }
        }
      });
    });
  }
};

const addItemsToForm = (
  shapeItems: ReportShapeItem[],
  newDictionary: NewDictionary,
  shapeDictionary: ShapeDictionaryType | null
) => {
  if (!shapeDictionary) {
    shapeItems.forEach((shapeItem) => {
      if (shapeItem.name === 'ports' || shapeItem.name === 'port') {
        newDictionary[shapeItem.name] = [];
      } else {
        newDictionary[shapeItem.name] = undefined;
      }
    });
  } else {
    Object.keys(shapeDictionary).forEach((key) => {
      if (key === 'ports' || key === 'port') {
        newDictionary[key] = shapeDictionary[key] || [];
      } else {
        newDictionary[key] = shapeDictionary[key] || undefined;
      }
    });
  }
};

const useExtractedShapeSchema = (
  shapeItems: ReportShapeItem[],
  type: ShapeType,
  instantShape: boolean | undefined
) => {
  return useCallback(
    () => createDynamicShapeZodSchema(shapeItems, type, instantShape),
    [shapeItems, instantShape, type]
  )();
};

export function useFormSetup(
  shapeItems: ReportShapeItem[],
  shapeItemData: ShapeDictionaryType | null,
  type: ShapeType,
  instantShapeData?: GeoJsonDictionary | null,
  instantShape?: boolean
) {
  const shapeSchema = useExtractedShapeSchema(shapeItems, type, instantShape);
  // Converting GeoJson shape to string id when using RegularShapeForm,
  // because the backend only need ids however for adHocShapes we need the GeoJson.
  if (
    !instantShape &&
    shapeItemData &&
    Object.values(shapeItemData).every(
      (item) => typeof item === 'object' && 'features' in item
    )
  ) {
    shapeItemData = convertGeoJsonShapeToStringId(
      shapeItemData as GeoJsonDictionary
    );
  }

  const defaultValues = useMemo(() => {
    const newDictionary: NewDictionary = {};
    if (type === 'multiShape') {
      addMultiShapeItemsToForm(
        shapeItems,
        newDictionary,
        shapeItemData as GeoJsonDictionary
      );
    } else {
      if (instantShapeData) {
        addItemsToForm(shapeItems, newDictionary, instantShapeData);
      } else {
        addItemsToForm(shapeItems, newDictionary, shapeItemData);
      }
    }
    return newDictionary;
  }, [shapeItemData, shapeItems, type, instantShapeData]);
  
  const form = useForm<ReportTemplateShapeValues>({
    resolver: zodResolver(shapeSchema),
    defaultValues: defaultValues,
  });
  
  // Reset form when shapeDictionary changes (e.g., when switching from quick to wizard)
  // Don't automatically reset - it causes infinite loops and isn't needed
  // The form will initialize correctly with defaultValues on mount
  // If you need to sync from quick to wizard, that should be handled differently
  
  return form;
}

function convertGeoJsonShapeToStringId(shapeDictionary: GeoJsonDictionary) {
  const newShapeDictionary: StringDictionary = {};
  Object.keys(shapeDictionary).forEach((key: string) => {
    const id = coerceIdToString(shapeDictionary[key].properties.id);
    newShapeDictionary[key] = id ?? '';
  });
  return newShapeDictionary;
}
